<div class="live-ops">
  <header class="live-ops__topbar">
    <div class="live-ops__brand">
      <div class="live-ops__brand-icon"><i class="fas fa-satellite-dish"></i></div>
      <div>
        <div class="live-ops__brand-title">Live Operations</div>
        <div class="live-ops__brand-subtitle">Updated {{ formatTime(lastUpdated) }}</div>
      </div>
    </div>

    <div class="mode-toggle">
      <button
        type="button"
        class="mode-toggle__btn"
        [class.mode-toggle__btn--active]="mode === 'live'"
        (click)="setMode('live')"
      >
        <i class="fas fa-bolt"></i> Live
      </button>
      <button
        type="button"
        class="mode-toggle__btn"
        [class.mode-toggle__btn--active]="mode === 'static'"
        (click)="setMode('static')"
      >
        <i class="fas fa-hand-pointer"></i> Static
      </button>
    </div>

    <div class="mode-toggle" ngbTooltip="Filter Live Logs by source" placement="bottom">
      <button
        type="button"
        class="mode-toggle__btn"
        [class.mode-toggle__btn--active]="sourceFilter === 'all'"
        (click)="setSourceFilter('all')"
      >
        <i class="fas fa-layer-group"></i> All
      </button>
      <button
        type="button"
        class="mode-toggle__btn"
        [class.mode-toggle__btn--active]="sourceFilter === 'ehr'"
        (click)="setSourceFilter('ehr')"
      >
        <i class="fas fa-hospital"></i> EHR
      </button>
      <button
        type="button"
        class="mode-toggle__btn"
        [class.mode-toggle__btn--active]="sourceFilter === 'audio'"
        (click)="setSourceFilter('audio')"
      >
        <i class="fas fa-microphone-alt"></i> Audio
      </button>
    </div>

    <button
      type="button"
      class="live-ops__ort-btn"
      (click)="openOrtMasters()"
      title="Open Medical Records &amp; Doctor Notes in a new tab"
    >
      <i class="fas fa-file-medical-alt"></i> Medical Records
    </button>

    <div class="live-ops__provider-select" *ngIf="providers.length">
      <i class="fas fa-user-md"></i>
      <select (change)="selectProvider($any($event.target).value || null)">
        <option value="">All Providers</option>
        <option *ngFor="let p of providers" [value]="p.provider_id">{{ p.description }}</option>
      </select>
    </div>

    <div class="live-ops__patient" *ngIf="currentPatientId; else waitingPatient">
      <div class="live-ops__patient-avatar" [style.background]="avatarColor">{{ initials }}</div>
      <div class="live-ops__patient-info">
        <div class="live-ops__patient-name">{{ currentPatientName || 'Unknown Patient' }}</div>
        <div class="live-ops__patient-meta" *ngIf="currentPatientMrn || currentPatientInsurance">
          <span *ngIf="currentPatientMrn">MRN {{ currentPatientMrn }}</span>
          <span class="live-ops__patient-meta-sep" *ngIf="currentPatientMrn && currentPatientInsurance">·</span>
          <span class="live-ops__patient-insurance" *ngIf="currentPatientInsurance">
            <i class="fas fa-id-card"></i> {{ currentPatientInsurance }}
          </span>
        </div>
      </div>
      <span class="live-ops__now-badge" *ngIf="mode === 'live'"><span class="live-dot"></span> Now Processing</span>
      <span class="live-ops__pinned-badge" *ngIf="mode === 'static'"><i class="fas fa-thumbtack"></i> Pinned</span>
    </div>
    <ng-template #waitingPatient>
      <div class="live-ops__patient live-ops__patient--empty">
        <i class="fas fa-hourglass-half"></i>
        {{ mode === 'live' ? 'Waiting for activity…' : 'Click a log entry to pin a patient' }}
      </div>
    </ng-template>

    <div class="live-ops__status">
      <span class="live-dot"></span>
      Live
    </div>
  </header>

  <div class="live-ops__grid">
    <!-- Top-left: Live Logs -->
    <section class="live-ops__panel live-ops__panel--logs">
      <div class="live-ops__panel-header">
        <span><i class="fas fa-stream"></i> Live Logs</span>
        <span class="live-ops__panel-hint" *ngIf="mode === 'static'">Click a log to pin its patient</span>
        <span class="live-ops__count" *ngIf="logs.length">{{ logs.length }}</span>
      </div>
      <div class="live-ops__panel-body" (scroll)="onLogsScroll($event)">
        <div class="live-ops__empty" *ngIf="logsLoading && !logs.length">
          <i class="fas fa-circle-notch fa-spin"></i> Loading…
        </div>
        <div class="live-ops__empty" *ngIf="!logsLoading && !logs.length">
          <i class="fas fa-inbox"></i> No activity yet.
        </div>
        <ul class="log-feed">
          <li
            class="log-feed__item"
            *ngFor="let log of logs; trackBy: trackByLogId"
            [ngClass]="{
              'log-feed__item--info': log.level === 'info',
              'log-feed__item--success': log.level === 'success',
              'log-feed__item--warn': log.level === 'warn',
              'log-feed__item--error': log.level === 'error',
              'log-feed__item--clickable': mode === 'static' && log.patient,
              'log-feed__item--selected': log.id === selectedLogId
            }"
            (click)="selectLog(log)"
          >
            <i class="fas log-feed__icon" [ngClass]="categoryIcon(log.category)"></i>
            <div class="log-feed__body">
              <div class="log-feed__top">
                <span class="log-feed__patient" *ngIf="log.patient">
                  {{ logPatientLabel(log) }}
                </span>
                <span class="log-feed__time">{{ formatTime(log.created_at) }}</span>
              </div>
              <div class="log-feed__message">{{ log.message }}</div>
            </div>
          </li>
        </ul>
        <div class="log-feed__loading-more" *ngIf="logsLoadingMore">
          <i class="fas fa-circle-notch fa-spin"></i> Loading older logs…
        </div>
      </div>
    </section>

    <!-- Top-right: Transcription & Doctor Notes -->
    <section class="live-ops__panel live-ops__panel--transcription">
      <div class="live-ops__panel-header">
        <span><i class="fas fa-microphone-alt"></i> Transcription &amp; Doctor Notes — Today</span>
        <span class="live-ops__count" *ngIf="dictations.length">{{ dictations.length }}</span>
      </div>
      <div class="live-ops__panel-body">
        <div class="live-ops__empty" *ngIf="!currentPatientId">
          <i class="fas fa-microphone-alt"></i> No patient in focus yet.
        </div>
        <div class="live-ops__empty" *ngIf="currentPatientId && dictationsLoading && !dictations.length">
          <i class="fas fa-circle-notch fa-spin"></i> Loading today's dictations…
        </div>
        <div class="live-ops__empty" *ngIf="currentPatientId && !dictationsLoading && !dictations.length">
          <i class="fas fa-calendar-day"></i> No dictations recorded today for this patient yet.
        </div>

        <div class="dict-card" *ngFor="let d of dictations; trackBy: trackByDictationId">
          <div class="dict-card__header">
            <div class="dict-card__header-meta">
              <span class="dict-card__time"><i class="fas fa-clock"></i> {{ formatDictationTime(d.created_at) }}</span>
              <span class="dict-card__provider" *ngIf="d.provider">{{ d.provider.description }}</span>
            </div>
          </div>

          <ng-container *ngIf="d.doctorNotesParsed?.doctor_notes as notes">
            <div class="dict-card__section" *ngIf="notes['History Of Present Illness']">
              <strong>History of Present Illness</strong>
              <p>{{ notes['History Of Present Illness'] }}</p>
            </div>
            <div class="dict-card__section" *ngIf="notes['Physical Examination']">
              <strong>Physical Examination</strong>
              <p>{{ notes['Physical Examination'] }}</p>
            </div>
            <div class="dict-card__section" *ngIf="notes['Diagnostics']">
              <strong>Diagnostics</strong>
              <p>{{ notes['Diagnostics'] }}</p>
            </div>
            <div class="dict-card__section" *ngIf="notes['Assessment/Plan']">
              <strong>Assessment / Plan</strong>
              <p>{{ notes['Assessment/Plan'] }}</p>
            </div>
          </ng-container>
        </div>
      </div>
    </section>

    <!-- Bottom-left: Action Items -->
    <section class="live-ops__panel live-ops__panel--actions">
      <div class="live-ops__panel-header">
        <span><i class="fas fa-tasks"></i> Action Items</span>
        <span class="live-ops__count" *ngIf="allActionItems.length">{{ allActionItems.length }}</span>
      </div>
      <div class="live-ops__panel-body">
        <div class="live-ops__empty" *ngIf="!allActionItems.length">
          <i class="fas fa-clipboard-check"></i> No action items yet today.
        </div>
        <div class="action-item" *ngFor="let item of sortedActionItems; trackBy: trackByActionItemId">
          <i
            class="fas action-item__status"
            [ngClass]="{
              'fa-check-circle action-item__status--accepted': actionItemStatus(item) === 'accepted',
              'fa-times-circle action-item__status--rejected': actionItemStatus(item) === 'rejected',
              'fa-clock action-item__status--pending': actionItemStatus(item) === 'pending'
            }"
          ></i>
          <div class="action-item__body">
            <div class="action-item__text">{{ item.action_item }}</div>
            <div class="action-item__time">{{ formatDictationTime(item.dictationTime) }}</div>
          </div>
          <div class="action-item__doc-actions" *ngIf="item.order?.lcd_coverage_details">
            <button
              type="button"
              class="action-item__doc-btn"
              (click)="viewMedicalNecessity(item)"
              title="View Medical Necessity"
            >
              <i class="fas fa-eye"></i> View
            </button>
            <button
              type="button"
              class="action-item__doc-btn"
              [disabled]="generatingPdfForItemId === item.id"
              (click)="downloadActionItemPdf(item)"
              title="Download Medical Necessity as PDF"
            >
              <i class="fas" [ngClass]="generatingPdfForItemId === item.id ? 'fa-circle-notch fa-spin' : 'fa-download'"></i>
              Download
            </button>
            <span
              *ngIf="medicalNecessityIndicator(item) as mnStatus"
              class="action-item__mn-indicator"
              [ngClass]="{
                'action-item__mn-indicator--approved': mnStatus === 'approved',
                'action-item__mn-indicator--missing': mnStatus === 'missing',
                'action-item__mn-indicator--not-approved': mnStatus === 'not-approved'
              }"
              [title]="medicalNecessityIndicatorLabel(item)"
            >
              <span class="action-item__mn-dot"></span>
              {{ medicalNecessityIndicatorLabel(item) }}
            </span>
          </div>
        </div>
      </div>
    </section>

    <!-- Bottom-right: Summary -->
    <section class="live-ops__panel live-ops__panel--billing">
      <div class="live-ops__panel-header">
        <span><i class="fas fa-align-left"></i> Summary</span>
      </div>
      <div class="live-ops__panel-body">
        <div class="live-ops__empty" *ngIf="!dictations.length">
          <i class="fas fa-align-left"></i> No summary data yet today.
        </div>

        <div class="billing-card" *ngFor="let d of dictations; trackBy: trackByDictationId">
          <div class="billing-card__header">
            <span><i class="fas fa-clock"></i> {{ formatDictationTime(d.created_at) }}</span>
            <span *ngIf="d.provider">{{ d.provider.description }}</span>
          </div>

          <!-- Summary -->
          <div class="billing-subpanel billing-subpanel--summary">
            <div class="billing-subpanel__title"><i class="fas fa-align-left"></i> Summary</div>
            <p class="billing-card__report" *ngIf="d.billingParsed?.billing_report; else noSummary">{{ d.billingParsed.billing_report }}</p>
            <ng-template #noSummary>
              <p class="billing-subpanel__empty">No billing summary available.</p>
            </ng-template>
          </div>
        </div>
      </div>
    </section>

    <!-- Middle-right: Office Visit Codes (from the dictation's billing analysis) -->
    <section class="live-ops__panel live-ops__panel--codes">
      <div class="live-ops__panel-header">
        <span><i class="fas fa-code"></i> Office Visit Codes — Today</span>
      </div>
      <div class="live-ops__panel-body">
        <div class="live-ops__empty" *ngIf="!dictations.length">
          <i class="fas fa-code"></i> No billing codes yet today.
        </div>

        <div class="dict-card" *ngFor="let d of dictations; trackBy: trackByDictationId">
          <div class="dict-card__header">
            <div class="dict-card__header-meta">
              <span class="dict-card__time"><i class="fas fa-clock"></i> {{ formatDictationTime(d.created_at) }}</span>
              <span class="dict-card__provider" *ngIf="d.provider">{{ d.provider.description }}</span>
            </div>
          </div>

          <div class="billing-subpanels" *ngIf="d.encounterCptCodes.length || d.encounterIcdCodes.length; else noEncounterCodes">
            <!-- CPT Codes -->
            <div class="billing-subpanel billing-subpanel--cpt" *ngIf="d.encounterCptCodes.length">
              <div class="billing-subpanel__title"><i class="fas fa-hashtag"></i> CPT Codes</div>
              <table class="billing-mini-table">
                <thead>
                  <tr><th>Code</th><th>Description</th><th>Reason</th></tr>
                </thead>
                <tbody>
                  <tr *ngFor="let code of d.encounterCptCodes">
                    <td>
                      <strong>{{ code.code }}</strong>
                      <span class="billing-mini-table__addon" *ngIf="code.isAddOn">Add-on</span>
                    </td>
                    <td>{{ code.description || '—' }}</td>
                    <td>{{ code.reason || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- ICD Codes -->
            <div class="billing-subpanel billing-subpanel--icd" *ngIf="d.encounterIcdCodes.length">
              <div class="billing-subpanel__title"><i class="fas fa-notes-medical"></i> ICD Codes</div>
              <table class="billing-mini-table">
                <thead>
                  <tr><th>Code</th><th>Related CPT</th><th>Reason</th></tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of d.encounterIcdCodes">
                    <td><strong>{{ row.code }}</strong></td>
                    <td>{{ row.cptCode }}</td>
                    <td>{{ row.reason || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <ng-template #noEncounterCodes>
            <p class="billing-subpanel__empty">No CPT/ICD codes identified for this dictation yet.</p>
          </ng-template>
        </div>
      </div>
    </section>

    <!-- Middle-right: Order Codes (medical necessity codes per order) -->
    <section class="live-ops__panel live-ops__panel--order-codes">
      <div class="live-ops__panel-header">
        <span><i class="fas fa-file-medical"></i> Order Codes — Today</span>
      </div>
      <div class="live-ops__panel-body">
        <div class="live-ops__empty" *ngIf="!dictations.length">
          <i class="fas fa-file-medical"></i> No order codes yet today.
        </div>

        <div class="billing-card" *ngFor="let d of dictations; trackBy: trackByDictationId">
          <div class="billing-card__header">
            <span><i class="fas fa-clock"></i> {{ formatDictationTime(d.created_at) }}</span>
            <span *ngIf="d.provider">{{ d.provider.description }}</span>
          </div>

          <div class="billing-order-group" *ngFor="let group of d.billingGroups; trackBy: trackByOrderId">
            <div class="billing-order-group__title">{{ group.description }}</div>

            <div class="billing-subpanels">
              <!-- CPT Codes -->
              <div class="billing-subpanel billing-subpanel--cpt" *ngIf="group.cptCodes.length">
                <div class="billing-subpanel__title"><i class="fas fa-hashtag"></i> CPT Codes</div>
                <table class="billing-mini-table">
                  <thead>
                    <tr><th>Code</th><th>Reason</th></tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let code of group.cptCodes">
                      <td><strong>{{ code.code }}</strong></td>
                      <td>{{ code.reason || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- ICD Codes -->
              <div class="billing-subpanel billing-subpanel--icd" *ngIf="group.icdCodes.length">
                <div class="billing-subpanel__title"><i class="fas fa-notes-medical"></i> ICD-10 Codes</div>
                <table class="billing-mini-table">
                  <thead>
                    <tr><th>Code</th><th>Reason</th></tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let row of group.icdCodes">
                      <td><strong>{{ row.code }}</strong></td>
                      <td>{{ row.reason || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <p class="billing-subpanel__empty billing-card__no-codes" *ngIf="!d.billingGroups.length">
            No billing codes identified yet for this dictation's orders.
          </p>
        </div>
      </div>
    </section>

    <!-- Top-right: Files -->
    <section class="live-ops__panel live-ops__panel--files">
      <div class="live-ops__panel-header">
        <span><i class="fas fa-folder-open"></i> Files</span>
      </div>
      <div class="live-ops__panel-body">
        <div class="live-ops__empty" *ngIf="!currentPatientId">
          <i class="fas fa-folder-open"></i> No patient in focus yet.
        </div>
        <div class="live-ops__empty" *ngIf="currentPatientId && !dictationsLoading && !dictations.length">
          <i class="fas fa-folder-open"></i> No files recorded today for this patient yet.
        </div>

        <div class="file-card" *ngFor="let d of dictations; trackBy: trackByDictationId">
          <div class="file-card__header">
            <span><i class="fas fa-clock"></i> {{ formatDictationTime(d.created_at) }}</span>
            <span *ngIf="d.provider">{{ d.provider.description }}</span>
          </div>

          <ul class="file-list">
            <li class="file-list__row" *ngFor="let row of d.fileRows; trackBy: trackByFileRowKey">
              <i class="fas file-list__icon" [ngClass]="row.icon"></i>
              <div class="file-list__body">
                <div class="file-list__label">{{ row.label }}</div>
                <div class="file-list__subtitle" *ngIf="row.subtitle">{{ row.subtitle }}</div>
              </div>
              <button type="button" class="file-list__btn" (click)="row.view()" title="View {{ row.label }}">
                <i class="fas fa-eye"></i>
              </button>
              <button
                type="button"
                class="file-list__btn"
                [disabled]="row.itemId && generatingPdfForItemId === row.itemId"
                (click)="row.download()"
                title="Download {{ row.label }} as PDF"
              >
                <i class="fas" [ngClass]="row.itemId && generatingPdfForItemId === row.itemId ? 'fa-circle-notch fa-spin' : 'fa-download'"></i>
              </button>
            </li>
          </ul>

          <p class="file-card__empty" *ngIf="!d.fileRows.length">No files available for this dictation yet.</p>
        </div>
      </div>
    </section>
  </div>
</div>
